import React, { useState, useEffect, useRef } from "react";
import { Typography, Stack } from "@mui/material";
import styles from "./AudioPlayer.module.css";
import MuiSlider from "../slider/MuiSlider";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ImageAssets from "../../../assets";
import getVideoDuration from "../../../utils/video-duration-formatter/VideoDuration";

interface ICustomAudioPlayerProps {
  audioUrl: any;
  data: any;
  index?: any;
  audioRefs?: any;
  handlePlayPause?: any;
  handleUnMute?: any;
  handleMute?: any;
  isThumbNail?: any;
  handlePauseWhenAudioEnded?: any
}

export const CustomAudioPlayer = ({
  audioUrl,
  data,
  index,
  audioRefs,
  handlePlayPause,
  handleMute,
  handleUnMute,
  isThumbNail,
  handlePauseWhenAudioEnded
}: ICustomAudioPlayerProps) => {

  const intervalRef: any = useRef();

  const [trackProgress, setTrackProgress] = useState(0);
  const [totalAudioDuration, setTotalAudioDuration] = useState<any>(0);
  const [currentPlayTime, setCurrentPlayTime] = useState(0);

  useEffect(() => {
    setTotalAudioDuration(audioRefs.current[index]?.duration);
    return () => {
      clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    audioRefs?.current[index]?.addEventListener("loadedmetadata", () => {
      setTotalAudioDuration(audioRefs.current[index]?.duration);
    });
    return () => {
      audioRefs?.current[index]?.removeEventListener("loadedmetadata", () => {
        setTotalAudioDuration(audioRefs.current[index].duration);
      });
    };
  }, [audioRefs?.current[index]]);

  useEffect(() => {
    if (data?.isPlaying) {
      clearInterval(intervalRef.current);

      intervalRef.current = setInterval(() => {
        setCurrentPlayTime(audioRefs.current[index]?.currentTime);
        setTrackProgress(audioRefs.current[index]?.currentTime);
      }, 1000);
    }
  }, [data?.isPlaying]);

  useEffect(() => {
    if (audioRefs.current[index]?.ended) {
      handlePauseWhenAudioEnded(index, data)
      setTotalAudioDuration(audioRefs.current[index].duration);
    }
  }, [audioRefs?.current[index]?.ended]);

  const handleSliderChange = (value: any) => {
    const valueCoversion: any = value;
    audioRefs.current[index].currentTime = valueCoversion;
    setTrackProgress(audioRefs.current[index].currentTime);
  };

  return (
    <React.Fragment>
      <audio
        ref={(el) => (audioRefs.current[index] = el)}
        className={styles["audio"]}
        src={audioUrl}
        muted={data?.isMute}
        id={`${audioUrl}+${index}+${data.id}`}
      ></audio>

      <Stack
        justifyContent={"space-between"}
        alignItems={"center"}
        columnGap={1.5}
        sx={{ width: "85%", margin: "auto", paddingTop: "7rem" }}
      >
        {isThumbNail ? null : data?.isMute ? (
          <img
            src={ImageAssets.ic_mute}
            onClick={(e) => {
              e.stopPropagation()
              handleUnMute(index, data)
            }}
            alt="volume"
            className={styles["volume-icon"]}
          />
        ) : (
          <img
            src={ImageAssets.ic_unmute}
            onClick={(e) => {
              e.stopPropagation()
              handleMute(index, data)
            }}
            alt="mute"
            className={styles["volume-icon"]}
          />
        )}

        {data?.isPlaying ? (
          <PauseRoundedIcon
            className={styles["audio-play-pause-icon"]}
            onClick={(e) => {
              e.stopPropagation()
              handlePlayPause(index, data)
            }}
          />
        ) : (
          <PlayArrowRoundedIcon
            className={styles["audio-play-pause-icon"]}
            onClick={(e) => {
              e.stopPropagation()
              handlePlayPause(index, data)
            }}
          />
        )}
        <MuiSlider
          trackProgress={trackProgress}
          duration={
            data?.isRecorded && totalAudioDuration === Infinity
              ? data.totalDuration
              : totalAudioDuration
          }
          handleSliderChange={handleSliderChange}
        />

        {isThumbNail ? null : data?.isPlaying ? (
          <Typography className={styles["duration"]}>
            {getVideoDuration(currentPlayTime)}
          </Typography>
        ) : (
          <Typography className={styles["duration"]}>
            {data?.isRecorded && totalAudioDuration === Infinity
              ? getVideoDuration(data.totalDuration)
              : getVideoDuration(totalAudioDuration)}
          </Typography>
        )}
      </Stack>
    </React.Fragment>
  );
};
